import isNil from 'lodash-es/isNil'
import isUndefined from 'lodash-es/isUndefined'
import pick from 'lodash-es/pick'

import {
  HTTP_STATUS_BAD_REQUEST,
  HTTP_STATUS_CONFLICT,
} from '../../constants/api'

import {
  APPOINTMENT_CANCEL_FOLLOW_UP_ACTIONS,
  APPOINTMENT_MOCK_FOLLOW_UP_RELEASED,
  APPOINTMENT_MOCK_REQUEST_SUMMARY_FIELDS,
  AppointmentCancelFollowUpAction,
  AppointmentCancelFollowUpBranch,
  AppointmentErrorCode,
} from './Appointment.constants'
import {
  getAppointmentMockError,
  getAppointmentMockTargetDueDate,
  getIsAppointmentMockFollowUpLive,
  getIsAppointmentMockSameInstant,
  toAppointmentMockDueDateEntry,
} from './Appointment.mock.utils'
import { getIsAppointmentExternalParticipant } from './Appointment.utils'

function getIsAppointmentMockParticipantNotified(participant) {
  return (
    getIsAppointmentExternalParticipant(participant) &&
    !isNil(participant.notified_at)
  )
}

function getAppointmentMockCancelledDueDate({ action, record }) {
  switch (action) {
    case AppointmentCancelFollowUpAction.revert:
      return record.follow_up.previous_due_date
    case AppointmentCancelFollowUpAction.clear:
      return null
    default:
      return record.requestDueDate
  }
}

export function getAppointmentMockDueDateChangedError(record) {
  return getAppointmentMockError({
    data: {
      code: AppointmentErrorCode.dueDateChanged,
      current_due_date: record.requestDueDate,
    },
    status: HTTP_STATUS_CONFLICT,
  })
}

export function toAppointmentMockAppliedRecord(record) {
  const targetDueDate = getAppointmentMockTargetDueDate(record)

  return {
    ...record,
    follow_up: {
      coupled: true,
      previous_due_date: getIsAppointmentMockFollowUpLive(record)
        ? record.follow_up.previous_due_date
        : record.requestDueDate,
      written_value: targetDueDate,
    },
    history: [
      toAppointmentMockDueDateEntry({
        from: record.requestDueDate,
        record,
        to: targetDueDate,
      }),
      ...record.history,
    ],
    requestDueDate: targetDueDate,
  }
}

export function toAppointmentMockFollowUpAnswer(record) {
  return {
    appointment: {
      follow_up_coupled: record.follow_up.coupled,
      follow_up_previous_due_date: record.follow_up.previous_due_date,
      follow_up_written_value: record.follow_up.written_value,
    },
    day_load_advisory: null,
    request: { due_date: record.requestDueDate, id: record.anchor.request.id },
  }
}

export function getAppointmentMockCancelBranch(record) {
  if (!record.anchor.request?.can_edit_due_date || !record.follow_up.coupled) {
    return AppointmentCancelFollowUpBranch.hidden
  }

  if (!getIsAppointmentMockFollowUpLive(record)) {
    return AppointmentCancelFollowUpBranch.stale
  }

  return isNil(record.follow_up.previous_due_date)
    ? AppointmentCancelFollowUpBranch.none
    : AppointmentCancelFollowUpBranch.previous
}

export function toAppointmentMockCancelPreview(record) {
  const { request } = record.anchor

  const notifiedCount = record.participants.filter(
    getIsAppointmentMockParticipantNotified
  ).length

  return {
    already_invited: notifiedCount > 0,
    follow_up: {
      branch: getAppointmentMockCancelBranch(record),
      current_due_date: record.requestDueDate,
      previous_value: record.follow_up.previous_due_date,
      request:
        request && pick(request, APPOINTMENT_MOCK_REQUEST_SUMMARY_FIELDS),
      written_value: record.follow_up.written_value,
    },
    n_notified: notifiedCount,
  }
}

export function toAppointmentMockClosedRecord({ data, record }) {
  const action = data.follow_up_action ?? null

  const isPreviewFallback =
    isNil(action) && isUndefined(data.expected_current_due_date)

  if (
    !isPreviewFallback &&
    !getIsAppointmentMockSameInstant(
      data.expected_current_due_date,
      record.requestDueDate
    )
  ) {
    throw getAppointmentMockDueDateChangedError(record)
  }

  if (
    !isPreviewFallback &&
    !APPOINTMENT_CANCEL_FOLLOW_UP_ACTIONS[
      getAppointmentMockCancelBranch(record)
    ].includes(action)
  ) {
    throw getAppointmentMockError({ status: HTTP_STATUS_BAD_REQUEST })
  }

  const dueDate = getAppointmentMockCancelledDueDate({ action, record })

  return {
    ...record,
    follow_up: APPOINTMENT_MOCK_FOLLOW_UP_RELEASED,
    history: getIsAppointmentMockSameInstant(dueDate, record.requestDueDate)
      ? record.history
      : [
          toAppointmentMockDueDateEntry({
            from: record.requestDueDate,
            record,
            to: dueDate,
          }),
          ...record.history,
        ],
    requestDueDate: dueDate,
  }
}
